import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { useAddressRecord } from 'src/graphql';
import { ScheduleCreator } from 'src/graphql/entities/stripe-subscription-schedule';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import {
  AddPreSoldMembershipForm,
  AddPreSoldMembershipFormValues,
  PreSoldMembershipSectionValues,
} from './add-pre-sold-membership-form';
import {
  AddPreSoldMembershipFormFields,
  PRE_SOLD_MEMBERSHIP_SECTION,
  preparePreSoldMembershipData,
  preSoldLengths,
} from './util';

export interface PsmPayload {
  amount: number;
  intervalUnit: 'month' | 'year';
  intervalCount: number;
  partnerProductId: string;
}

export const AddPreSoldMembershipModal = () => {
  const { closeModal } = useModalMethods(Modals.AddPreSoldMembership);
  const { isOpened, modalProps } = useModalState(Modals.AddPreSoldMembership);
  const { address } = useAddressRecord({ addressId: modalProps?.addressId || '' });

  const formMethods = useForm<PreSoldMembershipSectionValues>({
    mode: 'onChange',
    defaultValues: {
      [PRE_SOLD_MEMBERSHIP_SECTION]: {
        [AddPreSoldMembershipFormFields.PartnerProductId]: '',
        [AddPreSoldMembershipFormFields.Price]: 0,
        [AddPreSoldMembershipFormFields.LengthInMonths]: preSoldLengths[0].value,
      },
    },
  });

  const { formState, handleSubmit } = formMethods;

  const saveToSchedule = useCallback(
    async (data: AddPreSoldMembershipFormValues, addPsmToSchedule: ScheduleCreator) => {
      if (!address?.addressId || !address?.partnerId) {
        return;
      }

      const psmPayload = preparePreSoldMembershipData(data);

      closeModal();

      await addPsmToSchedule({
        addressId: address?.addressId,
        partnerId: address?.partnerId,
        preSoldMembership: psmPayload,
      });
    },
    [address?.addressId, address?.partnerId, closeModal],
  );

  const onValid = useCallback(
    (data: PreSoldMembershipSectionValues) => {
      if (!modalProps) return;

      const values = data[PRE_SOLD_MEMBERSHIP_SECTION];
      if (modalProps.onSubmit) return modalProps.onSubmit(values);

      return saveToSchedule(values, modalProps.addPsmToSchedule);
    },
    [modalProps, saveToSchedule],
  );

  const handleClose = useCallback(() => {
    formMethods.reset();
    closeModal();
  }, [closeModal, formMethods]);

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Add pre-sold membership"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'add-pre-sold-membership-cancel' },
        {
          label: 'Add membership',
          kind: 'primary',
          onClick: handleSubmit(onValid),
          disabled: !formState.isValid,
          testId: 'pre-sold-membership-submit',
        },
      ]}
      onClose={handleClose}
      data-testid="add-pre-sold-membership-modal"
    >
      <GenericForm<PreSoldMembershipSectionValues> formMethods={formMethods}>
        <AddPreSoldMembershipForm address={address} />
      </GenericForm>
    </ModalContainer>
  );
};
